import { BathDaiApplyResult } from '@core/models/models/bathe.model';
import { formatYmdWithDay } from './bath-pattern.helper';

/**
 * Turns a propagation apply-result into a view model the apply-result dialog renders: a status
 * summary (how many days succeeded / conflicted / failed / were superseded) and, per outcome, the
 * affected days grouped by room. Pure and display-text-free - the dialog maps each block key to its
 * Japanese label - so it is unit-testable without Angular.
 */

/** Fixed render order of the outcome blocks. */
export type ApplyResultBlockKey = 'updated' | 'added' | 'removed' | 'conflicted' | 'failed' | 'superseded';

/** Days of one outcome that fell in one room. `roomName` is empty when the room could not be resolved. */
export interface ApplyResultRoomGroup {
    roomName: string;
    /** Formatted 'yyyy/MM/dd（曜）', in the order the server returned them. */
    days: string[];
}

/** One outcome block: its count and the affected days grouped by room. */
export interface ApplyResultBlock {
    key: ApplyResultBlockKey;
    count: number;
    /** conflicted / failed - the dialog shows a "check 入浴管理" hint for these. */
    needsCheck: boolean;
    rooms: ApplyResultRoomGroup[];
}

/** The whole dialog view: header counts, the non-empty outcome blocks, and the two whole-dai notices. */
export interface ApplyResultView {
    summary: { success: number; conflicted: number; failed: number; superseded: number };
    blocks: ApplyResultBlock[];
    /** Master saved but propagation did not run; the dialog shows a notice naming the dai. */
    planFailed: boolean;
    /** The dai itself was not found; a whole-dai notice, no per-day list. */
    daiNotFound: boolean;
}

/**
 * Groups a list of cells by room, formatting each date with its weekday. Accepts both
 * `bathroom_{uid}/date_{yyyyMMdd}` (grouped under the room name) and bare `date_{yyyyMMdd}` (grouped
 * under an empty room name) - the latter lets the `updated` block fall back to `days` on an API that
 * does not yet return `updatedCells`.
 */
function groupCellsByRoom(cells: string[] | undefined, roomNameByUid?: ReadonlyMap<string, string>): ApplyResultRoomGroup[] {
    const order: string[] = [];
    const daysByRoom = new Map<string, string[]>();
    for (const cell of cells ?? []) {
        const cellMatch = /^bathroom_(.+)\/date_(\d{8})$/.exec(cell);
        const dayMatch = cellMatch ? null : /^date_(\d{8})$/.exec(cell);
        const roomName = cellMatch ? (roomNameByUid?.get(cellMatch[1]) ?? '') : '';
        const day = cellMatch ? formatYmdWithDay(cellMatch[2]) : dayMatch ? formatYmdWithDay(dayMatch[1]) : cell;
        if (!daysByRoom.has(roomName)) {
            daysByRoom.set(roomName, []);
            order.push(roomName);
        }
        daysByRoom.get(roomName)!.push(day);
    }
    return order.map(roomName => ({ roomName, days: daysByRoom.get(roomName)! }));
}

export function buildApplyResultView(result: BathDaiApplyResult, roomNameByUid?: ReadonlyMap<string, string>): ApplyResultView {
    const updated = result.updated ?? 0;
    const added = result.added ?? 0;
    const removed = result.removed ?? 0;
    const conflicted = result.conflicted ?? 0;
    const failed = result.failed ?? 0;
    const superseded = result.superseded ?? 0;

    const blocks: ApplyResultBlock[] = [];
    const addBlock = (key: ApplyResultBlockKey, count: number, cells: string[] | undefined, needsCheck = false): void => {
        if (count > 0) blocks.push({ key, count, needsCheck, rooms: groupCellsByRoom(cells, roomNameByUid) });
    };

    // Prefer updatedCells (carries the room); fall back to days (date-only) so the block still lists
    // dates against an API version that does not return updatedCells yet.
    addBlock('updated', updated, result.updatedCells?.length ? result.updatedCells : result.days);
    addBlock('added', added, result.addedCells);
    addBlock('removed', removed, result.removedCells);
    addBlock('conflicted', conflicted, result.conflictedCells, true);
    addBlock('failed', failed, result.failedCells, true);
    addBlock('superseded', superseded, result.supersededCells);

    return {
        summary: { success: updated + added + removed, conflicted, failed, superseded },
        blocks,
        planFailed: !!result.planFailed,
        daiNotFound: (result.daiNotFound ?? 0) > 0,
    };
}
